import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HeroSection.css';

const HeroSection: React.FC = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [showWords, setShowWords] = useState(false);
  const [isElegantScrolling, setIsElegantScrolling] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const words = [
    "You",
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWords(true);
      setIsElegantScrolling(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showWords && isElegantScrolling) {
      // Phase 1: Elegant scrolling for 6 seconds
      const elegantInterval = setInterval(() => {
        setCurrentWordIndex((prev) => (prev + 1) % words.length);
      }, 800);

      const elegantTimer = setTimeout(() => {
        clearInterval(elegantInterval);
        setIsElegantScrolling(false);
        setCurrentWordIndex(0); // Lock on "You"

        // Phase 2: Graceful focus on "You"
        setTimeout(() => {
          setIsFocused(true);
        }, 600);
      }, 6400);

      return () => {
        clearInterval(elegantInterval);
        clearTimeout(elegantTimer);
      };
    }
  }, [showWords, isElegantScrolling, words.length]);

  const isYouActive = currentWordIndex === 0 && !isElegantScrolling;

  return (
      <section className="hero-section">
        {/* Background Tiles */}
        <div className="background-tiles">
          <motion.div
              className="tile tile-1"
              initial={{ opacity: 0, scale: 0.8, y: 100 }}
              animate={{ opacity: 0.6, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
          >
            <div className="tile-glow"></div>
          </motion.div>

          <motion.div
              className="tile tile-2"
              initial={{ opacity: 0, scale: 0.8, y: 100 }}
              animate={{ opacity: 0.4, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
          >
            <div className="tile-glow"></div>
          </motion.div>

          <motion.div
              className="tile tile-3"
              initial={{ opacity: 0, scale: 0.8, y: 100 }}
              animate={{ opacity: 0.5, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
          >
            <div className="tile-glow"></div>
          </motion.div>

          <motion.div
              className="tile tile-4"
              initial={{ opacity: 0, scale: 0.8, y: 100 }}
              animate={{ opacity: 0.3, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
          >
            <div className="tile-glow"></div>
          </motion.div>

          <motion.div
              className="tile tile-5"
              initial={{ opacity: 0, scale: 0.8, y: 100 }}
              animate={{ opacity: 0.4, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.0 }}
          >
            <div className="tile-glow"></div>
          </motion.div>
        </div>

        {/* Floating Brand Content */}
        <div className="floating-content">
          {/* Main Logo - Crystal Clear and Big */}
          <motion.div
              className="brand-container"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 1.2, ease: "easeOut" }}
          >
            <h1 className="brand-name crystal-clear">
              {"Augments...".split("").map((letter, i) => (
                  <motion.span
                      key={i}
                      className="brand-letter"
                      initial={{ opacity: 0, y: 40 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 1.4 + i * 0.06,
                        duration: 0.6,
                        ease: "easeOut"
                      }}
                  >
                    {letter}
                  </motion.span>
              ))}
            </h1>
          </motion.div>

          {/* Elegant "You" with Refined Design */}
          <motion.div
              className="tagline-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 2 }}
          >
            <div className="tagline-wrapper">
              <div className={`word-display ${isYouActive ? 'you-active' : ''} ${isElegantScrolling ? 'elegant-scroll' : ''} ${isFocused ? 'focused' : ''}`}>
                <AnimatePresence mode="wait">
                  {showWords && (
                      <motion.span
                          key={currentWordIndex}
                          className={`dynamic-word ${currentWordIndex === 0 && !isElegantScrolling ? 'you-special' : ''} ${isElegantScrolling ? 'scrolling' : ''}`}
                          initial={{
                            opacity: 0,
                            y: isElegantScrolling ? 25 : 30,
                            filter: "blur(4px)",
                            scale: 0.95
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            filter: "blur(0px)",
                            scale: isYouActive && isFocused ? 1.08 : 1
                          }}
                          exit={{
                            opacity: 0,
                            y: isElegantScrolling ? -25 : -30,
                            filter: "blur(4px)",
                            scale: 0.95
                          }}
                          transition={{
                            duration: isElegantScrolling ? 0.4 : 0.7,
                            ease: "easeInOut"
                          }}
                      >
                        {words[currentWordIndex]}

                        {/* Refined "You" Glow Effects */}
                        {isYouActive && isFocused && (
                            <>
                              {/* Elegant Primary Glow */}
                              <motion.div
                                  className="you-glow elegant-primary"
                                  initial={{ opacity: 0, scale: 0.95 }}
                                  animate={{
                                    opacity: [0.15, 0.25, 0.15],
                                    scale: [0.95, 1.1, 0.95]
                                  }}
                                  transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                  }}
                              />

                              {/* Subtle Secondary Ring */}
                              <motion.div
                                  className="you-glow elegant-secondary"
                                  initial={{ opacity: 0, scale: 1.05 }}
                                  animate={{
                                    opacity: [0.1, 0.18, 0.1],
                                    scale: [1.05, 1.3, 1.05]
                                  }}
                                  transition={{
                                    duration: 5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 0.8
                                  }}
                              />

                              {/* Gentle Outer Aura */}
                              <motion.div
                                  className="you-glow elegant-aura"
                                  initial={{ opacity: 0, scale: 1.2 }}
                                  animate={{
                                    opacity: [0.05, 0.12, 0.05],
                                    scale: [1.2, 1.6, 1.2]
                                  }}
                                  transition={{
                                    duration: 6,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 1.5
                                  }}
                              />
                            </>
                        )}
                      </motion.span>
                  )}
                </AnimatePresence>

                {/* Elegant Scroll Frame */}
                {isElegantScrolling && (
                    <div className="elegant-frame">
                      <motion.div
                          className="frame-accent top"
                          animate={{ opacity: [0.2, 0.4, 0.2] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <motion.div
                          className="frame-accent bottom"
                          animate={{ opacity: [0.2, 0.4, 0.2] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.75 }}
                      />
                      <motion.div
                          className="frame-accent left"
                          animate={{ opacity: [0.15, 0.3, 0.15] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                      />
                      <motion.div
                          className="frame-accent right"
                          animate={{ opacity: [0.15, 0.3, 0.15] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1.3 }}
                      />
                    </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
  );
};

export default HeroSection;